import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRequisites } from "../../../_actions/coreActions";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  completeReservationPayment,
  fetchBookingPrice,
  submitDetailsForm,
} from "../../../api";
import { CardNumberElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Spinner from "../../partials/Spinner";
import SuccessPage from "./SuccessPage";
import { formatMoney, getTenantDomain } from "../../../helper";
import { useTranslation } from "react-i18next";
import PropertyPhotoMosaic from "../../partials/PropertyPhotoMosaic";
import { parseISO, getDate } from "date-fns";
import {
  setArrivalDate,
  setDepartureDate,
  setSelectedProperty,
} from "../../../_actions/searchActions";
import { Helmet } from "react-helmet";
import CollapsibleTextSection from "../../partials/CollapsibleTextSection";
import StayCalendarSection from "../../partials/StayCalendarSection";
import HotelReviewBox from "../../partials/HotelReviewBox";
import HotelAvailabilityBox from "../../partials/HotelAvailabilityBox";
import GoBackIcon from "../../../assets/images/back-icon.svg";
import HotelTestimonialBox from "../../partials/HotelTestimonialBox";
import BookingSummary from "../../partials/BookingSummary";
import StepTwoComponent from "../../partials/StepTwoComponent";
import CustomerReview from "../../partials/CustomerReview";
import TestimonialCard from "../../partials/TestimonialCard";
import HotelLocationMap from "../../partials/HotelLocationMap";
import PropertyAmenities from "../../partials/PropertyAmenities";
import { propertyMaxOccupancy } from "../../../helper/amenities";
import {
  fetchProperty,
  fetchPropertyReviews,
} from "../../../_actions/propertyActions";
import { resetCart, setCartRooms, set_tax, updateUpsellItem } from "../../../_actions/stateActions";
import { bookingPrice, cleaningFeeLines, fromServerPrice } from "../../../helper/bookingPrice";
import { cardErrors, contactErrors, payReservation, stripeLocale, withRetries } from "../../../helper/payment";
import { calculateCityTax } from "../../../helper/utils";
import BookingCard from "../../partials/BookingCard";
import {
  guestLimits,
  hasAllChildAges,
  reservationPersons,
  totalGuests as countGuests,
} from "../../../helper/guests";
import { bookingHint } from "../../../helper/bookingHint";
import useParentBookingBar from "../../partials/useParentBookingBar";
import { requestParentScrollToTop } from "../../../helper/embedViewport";
import {
  apartmentCartRooms,
  isGroupCovered,
  selectsApartmentAutomatically,
} from "../../../helper/roomSelection";
import { Language } from "../../partials/Language";
import leftChevronIcon from "../../../assets/images/leftChevronIcon.svg";
import { Link } from "react-router-dom";
import i18n from "../../../services/i18n";
import { getNameFromLocale } from "../../../helper";

const FormPage = () => {
  const [stripe, setStripe] = useState(null);
  const [error, setError] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [formError, setFormError] = useState({});
  const [elements, setElements] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const params = useParams();
  const [isChecked, setChecked] = useState(false);
  const [disabledDates, setDisabledDates] = useState([]);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherData, setVoucherData] = useState(null);
  const [showTestimonialBox, setShowTestimonialBox] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState([]);
  const [minimumStayDates, setMinimumStayDates] = useState([]);
  const [roomsUpdateKey, setRoomsUpdateKey] = useState(0);
  const [step, setStep] = useState(1);
  const [cardFieldStatus, setCardFieldStatus] = useState({
    cardNumber: { empty: true, complete: false },
    cardExpiry: { empty: true, complete: false },
    cardCvc: { empty: true, complete: false },
  });

  const { t } = useTranslation();

  useEffect(() => {
    async function fetchKeyAndInitializeStripe() {
      try {
        // A property with its own Stripe account is paid there.
        const res = await fetch(
          `${getTenantDomain(params.tenant)}/api/get-stripe-key?property_id=${params.id}`
        );
        if (!res.ok) {
          throw new Error("Failed to fetch Stripe key");
        }
        const data = await res.json();
        const stripe = await loadStripe(data.key, {
          stripeAccount: data.stripe_account_id,
        });
        setStripe(stripe);
      } catch (err) {
        setError(err.message);
      }
    }
    fetchKeyAndInitializeStripe();
  }, [params.tenant, params.id]);


  useEffect(() => {
    async function getBookedDates() {
      try {
        const res = await fetch(
          `${getTenantDomain(params.tenant)}/api/iframe/get-booked-dates/${params.id}`
        );
        if (!res.ok) {
          throw new Error("Failed to fetch booked dates");
        }
        const data = await res.json();

        const disabledDatesList = data.disabledDates.map((ele) => parseISO(ele.date));
        setDisabledDates(disabledDatesList);
        setMinimumStayDates(data.minimumStayDates);
      } catch (err) {
        setError(err.message);
      }
    }

    if (params.tenant && params.id) {
      getBookedDates();
    }
  }, [params.tenant, params.id]);

  //const upsells = useSelector((state) => state.core.upsells);
  const currency = useSelector((state) => state.core.currency);
  const dispatch = useDispatch();
  const { arrivalDate, departureDate, persons, selectedProperty } = useSelector(
    (state) => state.search
  );

  const isOneRoomRef = useRef(false);
  const lastDatesRef = useRef(null);

  const getPropertyDetails = useCallback(() => {
    // A new arrival without its departure yet is not a stay to price.
    if (arrivalDate && !(departureDate && moment(departureDate).isAfter(arrivalDate, "day"))) {
      return;
    }
    const options = {
      startDate: moment(arrivalDate).format("YYYY-MM-DD"),
      endDate: moment(departureDate).format("YYYY-MM-DD"),
      persons,
    };
    // Selection is kept on guest change for apartments, but always reset on date change
    const datesKey = `${options.startDate}_${options.endDate}`;
    const datesChanged =
      lastDatesRef.current !== null && lastDatesRef.current !== datesKey;
    lastDatesRef.current = datesKey;
    dispatch(fetchProperty(params.id, options, params.tenant));
    if (!isOneRoomRef.current || datesChanged) {
      setRoomsUpdateKey((prevKey) => prevKey + 1);
      dispatch(resetCart());
    }
  }, [arrivalDate, departureDate, persons, params.id, params.tenant, dispatch]);

  useEffect(() => {
    dispatch(fetchRequisites(params.tenant));
    dispatch(fetchPropertyReviews(params.tenant, params.id));
  }, [dispatch, params.tenant, params.id]);

  // Loads the property for the searched guests once every child has an age,
  // so choosing ages does not reload the rooms with half of them.
  useEffect(() => {
    if (persons && hasAllChildAges(persons)) {
      getPropertyDetails();
    }
  }, [persons, getPropertyDetails]);
  const property = useSelector((state) => state.property.property);
  const reviews = useSelector((state) => state.reviews.reviews);

  useEffect(() => {
    if (property) {
      isOneRoomRef.current = property.is_one_room == 1;
    }
  }, [property]);

  const [upsellValues, setUpsellValues] = useState({});
  const [userDetails, setUserDetails] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const { name, lastName, email, phone, message } = userDetails;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear the field-level error when user starts typing
    const errorKey = `userDetails.${name}`;
    if (formError[errorKey]) {
      setFormError((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const handleCardFieldChange = useCallback((elementType, state) => {
    setCardFieldStatus((prev) => ({
      ...prev,
      [elementType]: state,
    }));
    // Clear card field error when user interacts with it
    const keyMap = { cardName: "card.name", cardNumber: "card.number", cardExpiry: "card.expiry", cardCvc: "card.cvc" };
    const errorKey = keyMap[elementType];
    if (errorKey) {
      setFormError((prev) => {
        if (!prev[errorKey]) return prev;
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  }, []);

  const increaseUpsellValue = (ups) => {
    if (!upsellValues) return;
    setUpsellValues((prevState) => ({
      ...prevState,
      [ups.id]: {
        ...prevState[ups.id],
        value:
          (prevState[ups.id] && prevState[ups.id]["value"]
            ? prevState[ups.id]["value"]
            : 0) + 1,
        icon: ups.icon,
        price: ups.price,
        name: ups.code,
        type: ups.type,
        id: ups.id,
      },
    }));
  };

  const decreaseUpsellValue = (ups) => {
    if (!upsellValues) return;
    setUpsellValues((prevState) => ({
      ...prevState,
      [ups.id]: {
        ...prevState[ups.id],
        value:
          (prevState[ups.id] && prevState[ups.id]["value"]
            ? prevState[ups.id]["value"]
            : 0) - 1,
        icon: ups.icon,
        price: ups.price,
        name: ups.code,
        type: ups.type,
        id: ups.id,
      },
    }));
  };
  // Calendar nights, also across a change to or from summer time.
  const totalDays = useCallback(
    () =>
      Math.abs(
        moment(new Date(departureDate)).startOf("day").diff(moment(new Date(arrivalDate)).startOf("day"), "days")
      ),
    [arrivalDate, departureDate]
  );
  const totalHours = useCallback(() => {
    var start = moment(new Date(arrivalDate), "YYYY-MM-DD 02:00:ss");
    var end = moment(new Date(departureDate), "YYYY-MM-DD 11:00:ss");
    return Math.abs(moment.duration(start.diff(end)).asHours());
  }, [arrivalDate, departureDate]);

  const totalUpsell = useCallback(
    (item) => {
      let total = 0;
      if (item.value > 0) {
        let item_price = 0;
        switch (item.type) {
          case "stay":
            item_price = item.price;
            break;
          case "hour":
            item_price = item.price * totalHours();
            break;
          case "night_person":
            item_price = item.price * persons.adults * totalDays();
            break;
          default:
            item_price = item.price * totalDays();
            break;
        }
        total += item_price * item.value;
      }
      return total;
    },
    [persons.adults, totalDays, totalHours]
  );

  const paymentMessages = {
    failed: t(
      "payment_failed",
      "The payment did not go through. Please try again or use another payment method."
    ),
    authenticationFailed: t(
      "payment_authentication_failed",
      "The payment could not be verified with your bank. Please try again."
    ),
    priceChanged: t(
      "price_changed",
      "The price has changed. Please check the summary and confirm again."
    ),
  };

  // Books and pays with a payment method of the card or a wallet. Returns
  // whether the stay is booked.
  const payWith = async (paymentMethodId) => {
    setIsLoading(true);
    setErrorMessage("");
    const result = await payReservation({
      submit: (methodId, expectedAmount) =>
        submitDetailsForm(
          params.id,
          userDetails,
          methodId,
          { arrivalDate, departureDate, persons: reservationPersons(persons) },
          params.tenant,
          cart,
          expectedAmount
        ),
      complete: (paymentIntentId) =>
        completeReservationPayment(params.tenant, paymentIntentId),
      stripe,
      paymentMethodId,
      expectedAmount: serverPrice.price?.totalCents,
      messages: paymentMessages,
    });
    setIsLoading(false);

    if (result.status === "success") {
      setIsBooked(true);
      return true;
    }
    if (result.status === "price_changed" && result.price) {
      setServerPrice({ status: "ready", price: fromServerPrice(result.price) });
    }
    setErrorMessage(result.message);
    if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
      setFormError(result.fieldErrors);
    }
    return false;
  };

  const handleSubmit = async (stripe, elements) => {
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
      billing_details: {
        name: document.getElementById("inputCardholderName").value,
      },
    });
    if (error) {
      setIsLoading(false);
      setError(error.message);
      return;
    }
    await payWith(paymentMethod.id);
  };

  // Contact details and terms come first for every way of paying.
  const validateContact = () => {
    const errors = contactErrors(userDetails, isChecked, t);
    setFormError(errors);

    return Object.keys(errors).length === 0;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (serverPrice.status !== "ready" || isLoading) {
      return;
    }

    const errors = {
      ...contactErrors(userDetails, isChecked, t),
      ...cardErrors(
        document.getElementById("inputCardholderName")?.value,
        cardFieldStatus,
        t
      ),
    };
    setFormError(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (formRef && formRef.current) {
      handleSubmit(stripe, elements);
    }
  };

  useEffect(() => {
    dispatch(updateUpsellItem(upsellValues));
  }, [upsellValues, dispatch]);

  useEffect(() => {
    if (!selectedProperty) {
      if (!property) {
        // Call fetchProperty without options for initial load (will use base URL)
        dispatch(fetchProperty(params.id, null, params.tenant));
      } else {
        dispatch(setSelectedProperty(property));
      }
    }
  }, [selectedProperty, property, dispatch, params.id, params.tenant]);

  const cart = useSelector((state) => state.cart);
  const totalGuests = countGuests(persons);
  // Aparthotel groups split across apartments must be fully covered before continuing
  const groupCovered = isGroupCovered(property, cart?.rooms, totalGuests);

  // A single apartment needs no choice: it is put in the cart for the searched
  // guests and dates, and repriced whenever the property is loaded again.
  const autoSelectsApartment =
    selectsApartmentAutomatically(property) && String(property?.id) === String(params.id);
  useEffect(() => {
    if (!autoSelectsApartment || !arrivalDate || !departureDate) {
      return;
    }
    dispatch(setCartRooms(apartmentCartRooms(property, totalGuests)));
  }, [autoSelectsApartment, property, totalGuests, arrivalDate, departureDate, dispatch]);

  // City tax and the cleaning fee count from the first step, so every total
  // shows what the guest pays.
  const roomsChosen = cart?.rooms?.length > 0;
  const cityTax = useMemo(
    () =>
      roomsChosen
        ? calculateCityTax(
            arrivalDate,
            departureDate,
            persons?.adults ?? 0,
            Number(property?.city_tax_rate) || 0
          )
        : 0,
    [roomsChosen, arrivalDate, departureDate, persons?.adults, property?.city_tax_rate]
  );
  useEffect(() => {
    dispatch(set_tax(cityTax));
  }, [cityTax, dispatch]);
  const price = useMemo(
    () =>
      bookingPrice({
        rooms: cart?.rooms,
        upsell: cart?.upsell,
        voucher: cart?.voucher,
        cityTax,
        cleaningFee: cleaningFeeLines(property, cart?.rooms),
        totalUpsell,
      }),
    [cart?.rooms, cart?.upsell, cart?.voucher, cityTax, property, totalUpsell]
  );
  const total = price.total;

  // On the payment step the price comes from the server, so the summary,
  // the wallets and the charge show the same amount.
  const [serverPrice, setServerPrice] = useState({ status: "idle", price: null });
  const [priceAttempt, setPriceAttempt] = useState(0);
  const priceRequestKey =
    step === 2
      ? JSON.stringify({
          rooms: cart?.rooms,
          upsell: cart?.upsell,
          voucher: cart?.voucher?.code ?? null,
          arrivalDate,
          departureDate,
          persons,
        })
      : null;
  useEffect(() => {
    if (!priceRequestKey) {
      setServerPrice({ status: "idle", price: null });
      return undefined;
    }
    let current = true;
    setServerPrice((previous) => ({ ...previous, status: "loading" }));
    setErrorMessage("");
    withRetries(() =>
      fetchBookingPrice(
        params.id,
        { arrivalDate, departureDate, persons: reservationPersons(persons) },
        params.tenant,
        cart
      )
    )
      .then((serverTotal) => {
        if (current) setServerPrice({ status: "ready", price: fromServerPrice(serverTotal) });
      })
      .catch((requestError) => {
        if (!current) return;
        setServerPrice({ status: "error", price: null });
        // Nothing was paid yet, so this is about the price, not the payment.
        setErrorMessage(
          requestError?.response?.data?.errors?.msg?.[0] ||
            t("price_unavailable", "We could not load the price. Please try again.")
        );
      });

    return () => {
      current = false;
    };
    // The key holds everything the price depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceRequestKey, priceAttempt]);

  const minimumStayMap = minimumStayDates.reduce((acc, item) => {
    const key = Object.keys(item)[0];
    acc[key] = item[key];
    return acc;
  }, {});

  const getDayClassName = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const minStay = minimumStayMap[formattedDate];
    
    // Check if the date is in the disabled dates list (booked dates)
    const isBooked = disabledDates.some(disabledDate => {
      return moment(date).format("YYYY-MM-DD") === moment(disabledDate).format("YYYY-MM-DD");
    });
    
    let classes = [];
    
    if (isBooked) {
      classes.push("react-datepicker__day--booked");
    }
    
    if (minStay && minStay > 1) {
      classes.push("react-datepicker__day--has-minimum-stay");
    }
    
    return classes.join(" ");
  };

  const renderDayContents = (day, date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const minStay = minimumStayMap[formattedDate];
    
    if (minStay && minStay > 1) {
      const tooltipText = t("minimum_stay_hint", {
        count: minStay,
        defaultValue: "Minimum nights: {{count}}",
      });
      return (
        <span 
          className="date-tooltip"
          data-tooltip={tooltipText}
          title={tooltipText}
        >
          {getDate(date)}
        </span>
      );
    } else {
      return <span>{getDate(date)}</span>;
    }
  };

  const handleDatesChange = (dates) => {
    dispatch(setArrivalDate(dates.arrivalDate));
    dispatch(setDepartureDate(dates.departureDate));
  };

  const confirmDisabled =
    isLoading || !cart?.rooms?.length || !property?.room_types?.length || !groupCovered;
  // The details come only once every child has an age; the booking card
  // then marks the missing one.
  const [guestErrors, setGuestErrors] = useState(false);
  const [guestFocus, setGuestFocus] = useState(0);
  const childAgesComplete = hasAllChildAges(persons);
  useEffect(() => {
    if (childAgesComplete) setGuestErrors(false);
  }, [childAgesComplete]);
  const continueToDetails = () => {
    if (!childAgesComplete) {
      setGuestErrors(true);
      setGuestFocus((count) => count + 1);
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Embedded at full height, only the page around the iframe can scroll.
    requestParentScrollToTop();
  };
  const parentShowsBookingBar = useParentBookingBar({
    visible: step === 1 && !isBooked,
    label: t("total"),
    total: formatMoney(total, 2, currency),
    action: t("confirm"),
    disabled: confirmDisabled,
    onAction: continueToDetails,
  });

  const hint = bookingHint({
    arrivalDate,
    departureDate,
    singleApartment: autoSelectsApartment,
    childAgesComplete,
    hasRooms: cart?.rooms?.length > 0,
    groupCovered,
  });
  const hintText = () =>
    hint && t(hint.key, { guests: totalGuests, defaultValue: hint.fallback });

  const bookingCardProps = {
    step,
    arrivalDate,
    departureDate,
    disabledDates,
    minimumStayMap,
    onDatesChange: handleDatesChange,
    getDayClassName,
    renderDayContents,
    total,
    priceLines: price.lines,
    currency,
    formatMoney,
    guestLimits: guestLimits(property),
    // With the stay chosen, a missing age is the next step, so it is marked.
    guestErrors: guestErrors || (Boolean(arrivalDate && departureDate) && !childAgesComplete),
    guestFocus,
    roomHint: hintText(),
  };

  const summaryProps = {
    selectedProperty,
    tenant: params.tenant,
    arrivalDate,
    departureDate,
    guests: totalGuests,
    price: serverPrice.status === "ready" ? serverPrice.price : price,
    currency,
  };

  const walletProps =
    serverPrice.status === "ready" && serverPrice.price?.totalCents > 0
      ? {
          amount: serverPrice.price.totalCents,
          currency: serverPrice.price.currency,
          locale: stripeLocale(i18n.resolvedLanguage),
          billingDetails: {
            name: `${userDetails.name} ${userDetails.lastName}`.trim(),
            email: userDetails.email,
            phone: userDetails.phone,
          },
          validate: validateContact,
          pay: payWith,
          onError: (message) => setErrorMessage(message || paymentMessages.failed),
        }
      : null;

  const formRef = useRef();

  // if (!stripe) {
  //   return <p>{t("loading")}</p>;
  // }

  if (isBooked) {
    return <SuccessPage summary={summaryProps} />;
  }

  return (
    <main>
      <Helmet>
        <link
          href={`${getTenantDomain(params.tenant)}/iframe/style.css`}
          rel='stylesheet'
        />
      </Helmet>
      <section className='reservationSection'>
        <div className='container'>
          {/* <SearchBar disabledDates={disabledDates} /> */}
          <div className='header-content'>
            <Link to={`/${params.tenant}`} className='btnDefaultSmall iconBtn'>
              <img src={leftChevronIcon} alt='left Icn' />
              {t("back")}
            </Link>
            <Language />
          </div>
          <div className='appartBookingDetailWrap'>
            {step == 2 && (
              <div className='go-back-button'>
                <img
                  onClick={(e) => {
                    e.preventDefault();
                    setStep(1);
                  }}
                  src={GoBackIcon}
                  alt='back icon'
                />
                <p>
                  <span>{t("request_to_book")}</span>
                  <span>{t("step_2_2")}</span>
                </p>
              </div>
            )}
            {step === 1 && (
              <PropertyPhotoMosaic
                images={selectedProperty?.gallery || []}
                featuredImage={selectedProperty?.featured_image}
                tenant={params.tenant}
                title={property?.name}
              />
            )}
            <div className='colsHolder'>
              <div className='chCol chCol7-5'>
                {/* On a phone the summary comes before the details and payment */}
                {step === 2 && (
                  <div className='hide-on-desktop booking-summary-mobile'>
                    <BookingSummary {...summaryProps} />
                  </div>
                )}
                {step == 2 && (
                  <>
                    <header className='headingHead flexed-header'>
                      <h2>{t("contact_details")}</h2>
                      {/* <span>Step 1/2</span> */}
                    </header>
                    <form className='bookingForm'>
                      <div className='colsHolder'>
                        <div className='chCol chCol6'>
                          <div className='formGroup'>
                            <input
                              onChange={handleChange}
                              name='name'
                              value={name}
                              type='text'
                              className='formControl'
                              placeholder={t("placeholder_name")}
                            />
                            {formError["userDetails.name"] && (
                              <p className='error-message'>
                                {formError["userDetails.name"][0]}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className='chCol chCol6'>
                          <div className='formGroup'>
                            <input
                              onChange={handleChange}
                              value={lastName}
                              name='lastName'
                              type='text'
                              className='formControl'
                              placeholder={t("placeholder_last_name")}
                            />
                            {formError["userDetails.lastName"] && (
                              <p className='error-message'>
                                {formError["userDetails.lastName"][0]}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className='chCol chCol6'>
                          <div className='formGroup'>
                            <input
                              onChange={handleChange}
                              value={email}
                              name='email'
                              type='text'
                              className='formControl'
                              placeholder={t("placeholder_email")}
                            />
                            {formError["userDetails.email"] && (
                              <p className='error-message'>
                                {formError["userDetails.email"][0]}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className='chCol chCol6'>
                          <div className='formGroup'>
                            <input
                              onChange={handleChange}
                              value={phone}
                              name='phone'
                              type='text'
                              className='formControl'
                              placeholder={t("placeholder_phone")}
                            />
                            {formError["userDetails.phone"] && (
                              <p className='error-message'>
                                {formError["userDetails.phone"][0]}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className='chCol chCol12'>
                          <div className='formGroup mbZero'>
                            <textarea
                              onChange={handleChange}
                              value={message}
                              name='message'
                              type='text'
                              className='formControl'
                              placeholder={t("placeholder_message")}
                            ></textarea>
                            {formError["userDetails.message"] && (
                              <p className='error-message'>
                                {formError["userDetails.message"][0]}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
                {/* {step == 1 && ( */}
                <div style={{ display: step === 1 ? "block" : "none" }}>
                  {/* review box */}
                  <HotelReviewBox
                    title={property?.name}
                    rating={property?.score?.overall_score}
                    totalReviews={property?.score?.count}
                  />
                  {/* review box */}
                  <CollapsibleTextSection
                    title={t("description")}
                    html={getNameFromLocale(
                      property?.iframe_description || selectedProperty?.iframe_description,
                      i18n.resolvedLanguage
                    )}
                    className='property-description'
                  />
                  {/* booking card (mobile only, right column card is desktop only) */}
                  <div className='hide-on-desktop mobile-booking-card'>
                    <BookingCard {...bookingCardProps} />
                  </div>
                  <span className='horizontal-border'></span>

                  <div className='custBookDetailsWrap'>
                    {autoSelectsApartment ? (
                      <>
                        {/* A single apartment needs no room choice, only the stay */}
                        <StayCalendarSection
                          arrivalDate={arrivalDate}
                          departureDate={departureDate}
                          bookedDates={disabledDates}
                          minimumStayMap={minimumStayMap}
                          getDayClassName={getDayClassName}
                          renderDayContents={renderDayContents}
                          onChange={handleDatesChange}
                          location={property?.location?.city}
                        />
                        {arrivalDate && departureDate && !property?.room_types?.length && (
                          <div className='no-availability-message'>
                            <div className='no-availability-content'>
                              <span className='availability-emoji'>😔</span>
                              <h3>{t("no_availability_title", "No availability found")}</h3>
                              <p>{t("no_availability_message", "Unfortunately, there are no rooms available for your selected dates. Please try different dates.")}</p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : !arrivalDate || !departureDate ? (
                      <div className='no-availability-message'>
                        <div className='no-availability-content'>
                          <span className='availability-emoji'>📅</span>
                          <h3>{t("select_dates_title", "Please select your dates")}</h3>
                          <p>{t("select_dates_message", "Please select your arrival and departure dates to see available rooms.")}</p>
                        </div>
                      </div>
                    ) : property && property.room_types && property.room_types.length > 0 ? (
                      <HotelAvailabilityBox
                        key={roomsUpdateKey}
                        property={property}
                        tenant={params.tenant}
                        images={selectedProperty?.gallery || []}
                      />
                    ) : (
                      <div className='no-availability-message'>
                        <div className='no-availability-content'>
                          <span className='availability-emoji'>😔</span>
                          <h3>{t("no_availability_title", "No availability found")}</h3>
                          <p>{t("no_availability_message", "Unfortunately, there are no rooms available for your selected dates. Please try different dates.")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <PropertyAmenities
                    amenities={property?.amenities}
                    title={t("amenities_title", "Accommodation amenities")}
                    maxOccupancy={propertyMaxOccupancy(property)}
                  />
                  {/* location box */}
                  <HotelLocationMap selectedProperty={selectedProperty} />
                  {/* location box */}
                </div>
                {/* )} */}
                {/* step 2 data */}
                <StepTwoComponent
                  step={step}
                  //upsells={upsells}
                  params={params}
                  formatMoney={formatMoney}
                  upsellValues={upsellValues}
                  currency={currency}
                  increaseUpsellValue={increaseUpsellValue}
                  decreaseUpsellValue={decreaseUpsellValue}
                  voucherData={voucherData}
                  voucherCode={voucherCode}
                  setVoucherCode={setVoucherCode}
                  setVoucherData={setVoucherData}
                  stripe={stripe}
                  formRef={formRef}
                  setElements={setElements}
                  setError={setError}
                  setStripe={setStripe}
                  error={error}
                  handleSubmit={handleSubmit}
                  isChecked={isChecked}
                  setChecked={setChecked}
                  errorMessage={errorMessage}
                  walletProps={walletProps}
                  paymentReady={serverPrice.status === "ready"}
                  onRetryPrice={
                    serverPrice.status === "error" ? () => setPriceAttempt((attempt) => attempt + 1) : null
                  }
                  isLoading={isLoading}
                  submitHandler={submitHandler}
                  onCardFieldChange={handleCardFieldChange}
                  formError={formError}
                />
                {/* step 2 data */}
              </div>
              <div className='chCol chCol4-5'>
                <div className='stickycard hidden-xs'>
                  {step === 2 ? (
                    <BookingSummary {...summaryProps} />
                  ) : (
                    <BookingCard
                      {...bookingCardProps}
                      className='hidden-xs'
                      guestLiClassName='icnWrapList guest-dropdown-active'
                    />
                  )}
                  {step == 1 && (
                    <div className='submitBtn'>
                      <button
                        disabled={confirmDisabled}
                        className='btnPrimary primaryBtnColor'
                        onClick={continueToDetails}
                      >
                        {isLoading && <Spinner />}
                        {t("confirm")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {step === 1 && (
              <div className='relative before-after flex flex-col gap-10'>
                <h3>{t("customer_reviews")}</h3>
                {reviews.length === 0 ? (
                  <p className='reviews-empty'>{t("no_reviews_yet", { defaultValue: "No reviews yet" })}</p>
                ) : (
                <div className='flex flex-col gap-4'>
                  <CustomerReview direction='left'>
                    {reviews.length > 0 &&
                      reviews.map((item, index) => {
                        return (
                          <TestimonialCard
                            key={index}
                            item={item}
                            setShowTestimonialBox={setShowTestimonialBox}
                            setSelectedTestimonial={setSelectedTestimonial}
                          />
                        );
                      })}
                  </CustomerReview>
                  <CustomerReview direction='right'>
                    {reviews.length > 0 &&
                      reviews.map((item, index) => {
                        return (
                          <TestimonialCard
                            key={index}
                            item={item}
                            setSelectedTestimonial={setSelectedTestimonial}
                            setShowTestimonialBox={setShowTestimonialBox}
                          />
                        );
                      })}
                  </CustomerReview>
                </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      {step === 1 && !parentShowsBookingBar && (
        <div className='mobile-sticky-cta hide-on-desktop'>
          <div className='mobile-sticky-cta-total'>
            <span>{t("total")}</span>
            <strong>{formatMoney(total, 2, currency)}</strong>
          </div>
          <button
            disabled={confirmDisabled}
            className='btnPrimary primaryBtnColor'
            onClick={continueToDetails}
          >
            {isLoading && <Spinner />}
            {t("confirm")}
          </button>
        </div>
      )}
      <HotelTestimonialBox
        showTestimonialBox={showTestimonialBox}
        selectedTestimonial={selectedTestimonial}
        setShowTestimonialBox={setShowTestimonialBox}
      />
    </main>
  );
};

export default FormPage;
